import { useAspenFeatures } from "@/hooks/useAspenFeatures";
import { useNavLayout } from "@/context/NavLayoutContext";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { Tooltip } from "primereact/tooltip";
import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { C, themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { useHideAssessmentsForBundle } from "sj-ui-toolkit/src/hooks/useHideAssessmentsForBundle";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { sanitizeTestId } from "sj-ui-toolkit/src/utils/string";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RouteNode {
  name?: string;
  route?: string;
  nav?: boolean;
  navGroup?: boolean;
  navFeature?: string;
  icon?: string;
  routes?: Record<string, RouteNode>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isParametric = (route: string) => route.includes(":");

const NAV_TOOLTIP_TARGET = "admin-nav-tooltip-target";

const navLinkClass =
  (isCollapsed: boolean) =>
  ({ isActive }: { isActive: boolean }) =>
    C(
      "flex w-full items-center p-2.5 p-menuitem-link transition-all duration-300 ease-in-out",
      isCollapsed ? `gap-0 ${NAV_TOOLTIP_TARGET}` : "gap-2",
      isActive ? `text-color ${themeClasses.bgElevation3} rounded` : "text-secondary"
    );

// ---------------------------------------------------------------------------
// NavGroup — collapsible section header (no link, toggles children)
// ---------------------------------------------------------------------------

function NavGroup({ entry, depth = 0 }: { entry: RouteNode; depth?: number }) {
  const location = useLocation();
  const { isCollapsed } = useNavLayout();
  const childRoutes = entry.routes ? Object.values(entry.routes) : [];
  const children = childRoutes.filter(child => child.nav);

  const isChildActive = entry.route ? location.pathname.startsWith(entry.route) : false;
  const [expanded, setExpanded] = useState(isChildActive);

  const childContainerId = `nav-group-${entry.name?.toLowerCase().replace(/\s+/g, "-")}`;

  useEffect(() => {
    if (isChildActive) setExpanded(true);
  }, [isChildActive]);

  // Hover panel for the rail
  const overlayRef = useRef<OverlayPanel>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }
  };

  const scheduleHideOverlay = () => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => overlayRef.current?.hide(), 150);
  };

  const showOverlay = (e: React.SyntheticEvent) => {
    if (!isCollapsed || children.length === 0) return;
    clearHideTimeout();
    overlayRef.current?.show(e, e.currentTarget as HTMLElement);
  };

  useEffect(() => {
    if (!isCollapsed) overlayRef.current?.hide();
  }, [isCollapsed]);

  useEffect(() => clearHideTimeout, []);

  // In the rail, the hover popover handles navigation; clicking only toggles the inline accordion
  const handleClick = () => {
    if (isCollapsed) return;
    setExpanded(prev => !prev);
  };

  const isGroupOpen = expanded && !isCollapsed;
  const gapClass = isCollapsed ? "gap-0" : "gap-2";
  const isRailActive = isCollapsed && isChildActive;
  const colorClass = isRailActive
    ? `text-color ${themeClasses.bgElevation3} rounded`
    : isOverlayVisible
      ? "text-color"
      : "text-secondary";

  return (
    <div>
      <button
        type="button"
        aria-expanded={isGroupOpen}
        aria-controls={childContainerId}
        aria-label={entry.name ?? ""}
        data-testid={childContainerId}
        onClick={handleClick}
        onMouseEnter={showOverlay}
        onMouseLeave={scheduleHideOverlay}
        className={C(
          "flex w-full cursor-pointer items-center p-2.5 p-menuitem-link transition-all duration-300 ease-in-out",
          gapClass,
          isCollapsed ? "justify-start" : "justify-between",
          colorClass
        )}
        style={!isCollapsed && depth > 0 ? { paddingLeft: `${0.75 + depth * 0.75}rem` } : undefined}
      >
        <span
          className={`flex min-w-0 items-center overflow-hidden transition-all duration-300 ease-in-out ${gapClass}`}
        >
          {entry.icon && <i className={`${entry.icon} shrink-0`} aria-hidden="true" />}
          <span
            className={`whitespace-nowrap transition-all duration-300 ease-in-out ${isCollapsed ? "max-w-0 opacity-0" : "max-w-48 opacity-100"}`}
          >
            {entry.name}
          </span>
        </span>
        <i
          className={`pi pi-chevron-down body-xs shrink-0 transition-all duration-300 ease-in-out ${isCollapsed ? "max-w-0 opacity-0" : "max-w-4 opacity-100"} ${isGroupOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        id={childContainerId}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${isGroupOpen ? "max-h-96" : "max-h-0"}`}
      >
        {children.length > 0 && (
          <div className="space-y-1">
            {children.map((child, idx) => (
              <NavItem key={child.name ? child.name + idx : idx} entry={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
      {isCollapsed && children.length > 0 && (
        <OverlayPanel
          ref={overlayRef}
          showCloseIcon={false}
          onShow={() => setIsOverlayVisible(true)}
          onHide={() => setIsOverlayVisible(false)}
          onMouseEnter={clearHideTimeout}
          onMouseLeave={scheduleHideOverlay}
          pt={{
            root: { className: "!left-14 !-mt-8 before:hidden after:hidden" },
            content: { className: "p-1" },
          }}
        >
          <div className="flex min-w-40 flex-col gap-1">
            {children.map((child, idx) => {
              if (!child.name || !child.route || isParametric(child.route)) return null;
              return (
                <NavLink
                  key={child.route + idx}
                  to={child.route}
                  className={({ isActive }) =>
                    C(navLinkClass(false)({ isActive }), "body-sm hover:!text-[var(--text-color)]")
                  }
                >
                  {child.icon && <i className={`${child.icon} shrink-0`} aria-hidden="true" />}
                  <span className="whitespace-nowrap">{child.name}</span>
                </NavLink>
              );
            })}
          </div>
        </OverlayPanel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NavItem — recursive side-panel link
// ---------------------------------------------------------------------------

function NavItem({ entry, depth = 0 }: { entry: RouteNode; depth?: number }) {
  const { isCollapsed } = useNavLayout();

  if (entry.navGroup) {
    return <NavGroup entry={entry} depth={depth} />;
  }

  const hasLink = entry.name && entry.route && !isParametric(entry.route);
  const childRoutes = entry.routes ? Object.values(entry.routes) : [];
  const children = childRoutes.filter(child => child.nav);

  return (
    <>
      {hasLink && (
        <NavLink
          to={entry.route!}
          end={entry.route === AdminAppRoutes.routes.dashboard.route}
          className={navLinkClass(isCollapsed)}
          data-testid={`nav-link-${sanitizeTestId(entry.name ?? "")}`}
          aria-label={isCollapsed ? (entry.name ?? "") : undefined}
          data-pr-tooltip={isCollapsed ? (entry.name ?? "") : undefined}
          data-pr-position="right"
          style={
            !isCollapsed && depth > 0 ? { paddingLeft: `${0.75 + depth * 0.75}rem` } : undefined
          }
        >
          {entry.icon && <i className={`${entry.icon} shrink-0`} aria-hidden="true" />}
          <span
            className={`whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out ${isCollapsed ? "max-w-0 opacity-0" : "max-w-48 opacity-100"}`}
          >
            {entry.name}
          </span>
        </NavLink>
      )}

      {children.length > 0 && (
        <div
          className={`space-y-1 overflow-hidden transition-all duration-300 ease-in-out ${isCollapsed ? "max-h-0" : "max-h-96"}`}
        >
          {children.map((child, idx) => (
            <NavItem key={child.name ? child.name + idx : idx} entry={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sidepanel
// ---------------------------------------------------------------------------

export default function Sidepanel() {
  const { features, isBackOfficeAspenEnabled, isLoading } = useAspenFeatures({ enabled: true });
  const { auditLogUi } = useFlags();
  const { isFeatureEnabled } = useSettingsContext();
  const { isRestrictedBundle } = useUserContext();
  const { isCollapsed, toggleCollapsed } = useNavLayout();
  const hideQuests = isRestrictedBundle;
  const isAiAdvantageOnly = useHideAssessmentsForBundle();

  const navEntries = useMemo(() => {
    const entries = (Object.values(AdminAppRoutes.routes) as RouteNode[]).filter(entry => {
      if (entry.nav !== true) return false;
      if (isAiAdvantageOnly && entry.route === AdminAppRoutes.routes.assessments.route)
        return false;
      if (entry.route === AdminAppRoutes.routes.auditLog.route && auditLogUi !== true) {
        return false;
      }
      if (entry.navFeature && !isFeatureEnabled(entry.navFeature)) return false;
      if (
        entry.route === AdminAppRoutes.routes.aspen.route &&
        (isLoading || !isBackOfficeAspenEnabled)
      ) {
        return false;
      }
      return true;
    });

    return entries.map(entry => {
      if (isAiAdvantageOnly && entry.route === AdminAppRoutes.routes.reporting.route) {
        return {
          ...entry,
          routes: {
            ...entry.routes,
            assessments: {
              ...entry.routes?.assessments,
              nav: false,
            },
          },
        };
      }

      if (entry.route === AdminAppRoutes.routes.paths.route) {
        if (!hideQuests || !entry.routes) {
          return entry;
        }

        const filteredRoutes: Record<string, RouteNode> = {};
        Object.entries(entry.routes).forEach(([key, child]) => {
          if (child.route === AdminAppRoutes.routes.paths.routes.quests.route) {
            filteredRoutes[key] = { ...child, nav: false };
          } else {
            filteredRoutes[key] = child;
          }
        });

        return { ...entry, name: "Paths", routes: filteredRoutes };
      }

      if (entry.route !== AdminAppRoutes.routes.aspen.route) {
        return entry;
      }

      const aspenRoutes = entry.routes ?? {};
      const filteredAspenRoutes: Record<string, RouteNode> = {};
      if (aspenRoutes.settings) {
        filteredAspenRoutes.settings = { ...aspenRoutes.settings, nav: true };
      }
      if (aspenRoutes.adapt && features.adapt) {
        filteredAspenRoutes.adapt = { ...aspenRoutes.adapt, nav: true };
      }
      if (aspenRoutes.assist && features.assist) {
        filteredAspenRoutes.assist = { ...aspenRoutes.assist, nav: true };
      }
      if (aspenRoutes.guardian && features.guardian) {
        filteredAspenRoutes.guardian = { ...aspenRoutes.guardian, nav: true };
      }

      return {
        ...entry,
        navGroup: true,
        routes: filteredAspenRoutes,
      };
    });
  }, [
    features.adapt,
    features.assist,
    features.guardian,
    isBackOfficeAspenEnabled,
    isFeatureEnabled,
    isLoading,
    hideQuests,
    isAiAdvantageOnly,
    auditLogUi,
  ]);

  return (
    <aside
      data-testid="admin-sidebar"
      className={`fixed left-1 ${isCollapsed ? "w-10" : "w-60"} flex flex-col transition-all duration-300 print:hidden`}
      style={{ top: "12px", height: "calc(100vh - 12px)" }}
    >
      {/* Spacer matching fixed header height — kept outside the scroll container so the scrollbar track starts below the header */}
      <div className="h-[60px] flex-shrink-0" aria-hidden="true" />
      <div className="flex flex-shrink-0 justify-start">
        <Button
          type="button"
          link
          icon={`pi ${isCollapsed ? "pi-angle-double-right" : "pi-angle-double-left"}`}
          className="text-color"
          onClick={toggleCollapsed}
          aria-expanded={!isCollapsed}
          aria-controls="admin-sidenav"
          aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
          data-testid="sidebar-toggle"
        />
      </div>
      <nav
        id="admin-sidenav"
        className={`flex flex-1 flex-col overflow-y-auto pb-4 transition-all duration-300 ease-in-out ${isCollapsed ? "gap-0" : "gap-2"} ${themeClasses.scrollbar}`}
      >
        {navEntries.map((entry, idx) => (
          <NavItem key={entry.name ? entry.name + idx : idx} entry={entry} />
        ))}
      </nav>
      {isCollapsed && <Tooltip target={`.${NAV_TOOLTIP_TARGET}`} />}
    </aside>
  );
}
